// Public surveys module — high-level API exposed via `layers.surveys`.
//
// Owns:
//   - delegating to the WASM survey manager (definitions, targeting, auto-events)
//   - building the targeting context from window.location + caller-supplied data
//   - the `SurveyRenderer` lifecycle (one popup at a time)
//   - persistence of the show-history map across sessions (via persistence backend)
import type { LayersCore, SurveyDefinition, SurveyResponse } from '@layers/core-wasm';

import { SurveyRenderer } from './renderer.js';

export interface SurveysModuleOptions {
  /** When false, the module never renders chrome and `getActive()` always returns []. */
  enabled?: boolean;
  /**
   * Per-page client-side sample rate (0..=1). Independent of any server-side
   * sampling — applied AFTER targeting evaluation.
   */
  sampleRate?: number;
  /**
   * Optional caller-supplied person properties for targeting evaluation.
   * Wrappers can pass the same value used for feature-flag evaluation.
   */
  personProperties?: Record<string, unknown>;
  /**
   * Optional feature-flag resolver. Returns the truthy value of a flag.
   * Wrappers wire this to their existing feature-flag engine.
   */
  resolveFeatureFlag?: (flagKey: string) => boolean | string | null | undefined;
  /**
   * If provided, a localStorage-style persistence interface used to persist
   * the per-survey-id show history across sessions. The browser SDK passes
   * its existing persistence backend automatically.
   */
  persistence?: {
    read(key: string): string | null;
    write(key: string, value: string): void;
  };
}

const SHOW_HISTORY_STORAGE_KEY = 'layers:surveys:show-history';

export class SurveysModule {
  private renderer: SurveyRenderer;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingSurveyId: string | null = null;

  constructor(
    private readonly core: LayersCore,
    private readonly options: SurveysModuleOptions = {}
  ) {
    this.renderer = new SurveyRenderer({
      onShown: (id) => this.handleShown(id),
      onDismissed: (id) => this.handleDismissed(id),
      onSubmit: (id, response) => this.handleSubmitted(id, response)
    });

    // Hydrate show-history from persistence on construction so frequency caps
    // survive page reloads.
    this.hydrateShowHistory();
  }

  /** Enabled? (config flag set at init). */
  get enabled(): boolean {
    return this.options.enabled !== false;
  }

  /** All known survey ids cached from `/config`. */
  knownIds(): string[] {
    return this.core.knownSurveyIds();
  }

  /**
   * Return surveys currently eligible to show given the current page URL +
   * the resolved feature-flag values + caller-supplied person properties.
   */
  getActive(): SurveyDefinition[] {
    if (!this.enabled) return [];
    return this.core.getActiveSurveys(this.buildContext());
  }

  /**
   * Manually render a survey by id. If the id doesn't exist or the user has
   * already dismissed/completed it this session, returns false.
   */
  show(surveyId: string): boolean {
    if (!this.enabled || typeof document === 'undefined') return false;
    if (this.renderer.isShowing()) return false;
    if (this.pendingSurveyId === surveyId) return true;

    const all = this.getActive();
    const survey = all.find((s) => s.id === surveyId);
    if (!survey) return false;
    this.scheduleRender(survey);
    return true;
  }

  /**
   * Auto-mode: pick the first eligible survey (sorted by id) and render it.
   * Respects `display.delay_ms` and `sampleRate`.
   */
  showFirstEligible(): SurveyDefinition | null {
    if (!this.enabled) return null;
    if (this.renderer.isShowing() || this.pendingSurveyId !== null) return null;
    const actives = this.getActive();
    if (actives.length === 0) return null;
    const survey = actives[0];
    if (!survey) return null;
    if (!this.passesSampleRate()) return null;
    this.scheduleRender(survey);
    return survey;
  }

  /** Dismiss the currently-shown survey (if any). */
  dismiss(surveyId?: string): void {
    if (this.pendingTimeout && (!surveyId || surveyId === this.pendingSurveyId)) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
      this.pendingSurveyId = null;
    }
    if (this.renderer.isShowing()) {
      const showing = this.renderer.currentSurveyId();
      if (!surveyId || surveyId === showing) {
        this.renderer.dismiss();
      }
    }
  }

  /**
   * Update caller-supplied targeting state (person properties, sampleRate, etc.)
   * without re-instantiating the module.
   */
  configure(patch: Partial<SurveysModuleOptions>): void {
    Object.assign(this.options, patch);
  }

  // ── Internals ────────────────────────────────────────────────────────

  private buildContext(): import('@layers/core-wasm').SurveyTargetingContext {
    const featureFlagValues: Record<string, boolean> = {};
    const resolver = this.options.resolveFeatureFlag;
    if (resolver) {
      // Walk ALL definitions (not just active ones — flag-gated surveys are
      // never active until their flag is resolved, so iterating the active
      // list creates a circular dependency).
      for (const def of this.allDefinitions()) {
        const flag = def?.targeting?.feature_flag;
        if (flag) {
          const v = resolver(flag);
          if (typeof v === 'boolean') featureFlagValues[flag] = v;
          else if (typeof v === 'string') featureFlagValues[flag] = v.length > 0 && v !== 'false';
        }
      }
    }
    const ctx: import('@layers/core-wasm').SurveyTargetingContext = {
      person_properties: this.options.personProperties ?? {},
      feature_flag_values: featureFlagValues
    };
    if (typeof window !== 'undefined' && window.location?.href) {
      ctx.url = window.location.href;
    }
    return ctx;
  }

  private allDefinitions(): SurveyDefinition[] {
    try {
      return JSON.parse(this.core.surveyDefinitionsJson()) as SurveyDefinition[];
    } catch {
      return [];
    }
  }

  private scheduleRender(survey: SurveyDefinition): void {
    const delay = Math.max(0, survey.display?.delay_ms ?? 0);
    this.pendingSurveyId = survey.id;
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      this.pendingSurveyId = null;
      // Re-check eligibility right before rendering — caller may have
      // changed state during the delay window.
      const stillEligible = this.getActive().some((s) => s.id === survey.id);
      if (!stillEligible) return;
      this.renderer.show(survey);
    }, delay);
  }

  private handleShown(surveyId: string): void {
    this.core.markSurveyShown(surveyId);
    this.persistShowHistory();
  }

  private handleDismissed(surveyId: string): void {
    this.core.markSurveyDismissed(surveyId);
  }

  private handleSubmitted(surveyId: string, response: SurveyResponse): void {
    this.core.submitSurveyResponse(surveyId, response);
  }

  private passesSampleRate(): boolean {
    const rate = this.options.sampleRate;
    if (rate === undefined || rate >= 1) return true;
    if (rate <= 0) return false;
    return Math.random() < rate;
  }

  private hydrateShowHistory(): void {
    const { persistence } = this.options;
    if (!persistence) return;
    try {
      const raw = persistence.read(SHOW_HISTORY_STORAGE_KEY);
      if (raw) this.core.seedSurveyShowHistory(raw);
    } catch {
      // Best effort
    }
  }

  private persistShowHistory(): void {
    const { persistence } = this.options;
    if (!persistence) return;
    try {
      persistence.write(SHOW_HISTORY_STORAGE_KEY, this.core.surveyShowHistoryJson());
    } catch {
      // Best effort
    }
  }
}

export type { SurveyDefinition, SurveyResponse } from '@layers/core-wasm';
